<template>
  <div class="simple-upload-terms" data-testid="simple-upload-terms">
    <h2 class="text-h6 mb-3">{{ $t('simple_mode.upload_title') }}</h2>

    <div class="mb-4">
      <v-combobox
        v-model="termChips"
        :label="$t('simple_mode.detect_label')"
        :hint="$t('simple_mode.detect_hint')"
        persistent-hint
        multiple
        chips
        closable-chips
        clearable
        density="comfortable"
        variant="outlined"
        data-testid="simple-terms-input"
        @update:model-value="onTermsChanged"
      >
        <template #chip="{ item, props }">
          <v-chip v-bind="props" class="term-chip">
            <span
              class="term-color-swatch mr-1"
              :style="{ backgroundColor: rgbCss(termColorForChip(item.raw)) }"
            />
            {{ chipLabel(item.raw) }}
            <v-menu :close-on-content-click="false" location="bottom">
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  icon
                  size="x-small"
                  variant="text"
                  class="ml-1"
                  data-testid="simple-term-color-btn"
                  @click.stop
                >
                  <v-icon size="x-small">mdi-palette</v-icon>
                </v-btn>
              </template>
              <v-color-picker
                :model-value="hexFromRgb(termColorForChip(item.raw))"
                mode="hex"
                hide-inputs
                @update:model-value="(hex) => setTermColor(chipLabel(item.raw), hex)"
              />
            </v-menu>
          </v-chip>
        </template>
      </v-combobox>
    </div>

    <!-- Hidden uploader: file picker is opened only via the Add files button -->
    <multi-file-uploader
      ref="uploader"
      class="simple-upload-terms__uploader"
      :post_url="apiHost + '/multi_images_import'"
      :params="uploadParams"
      :upload_trigger="uploadTrigger"
      :trigger_reset_form="triggerReset"
      :hide_input="true"
      @files-selected="onFilesSelected"
      @multi-upload-complete="onUploadComplete"
      @success-messages="onSuccess"
      @failure-messages="onFailure"
      @progress-changed="progress = $event"
    />

    <div class="mt-3">
      <v-btn
        color="primary"
        :disabled="terms.length === 0 || uploading"
        :loading="uploading"
        data-testid="simple-add-files-btn"
        @click="openFilePicker"
      >
        {{ hasUploadedOnce ? $t('simple_mode.add_more_files') : $t('simple_mode.add_files') }}
      </v-btn>
      <v-progress-linear
        v-if="uploading"
        class="mt-3"
        :model-value="progress || 0"
        height="8"
        rounded
        color="primary"
        data-testid="simple-upload-progress"
      />
      <div v-if="uploading && progress != null" class="text-caption mt-1">
        {{ Math.round(progress) }}%
      </div>
    </div>
  </div>
</template>

<script>
import MultiFileUploader from '@/components/MultiFileUploader.vue';
import { DEFAULT_LABEL_COLORS, pickRandomAvailableColors } from '@/utils/labelCsv';

const ON_UPLOAD_LLM_JSON = 'on_upload_llm_json';
const ON_UPLOAD_PIPELINE_JSON = 'on_upload_pipeline_json';

function rgbToHex(rgb) {
  const parts = String(rgb || '').split(',').map((p) => parseInt(p.trim(), 10));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
    return '#f94144';
  }
  return `#${parts.slice(0, 3).map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) {
    return DEFAULT_LABEL_COLORS[0];
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

export default {
  name: 'SimpleUploadAndTerms',
  components: { MultiFileUploader },
  data() {
    return {
      termChips: [],
      terms: [],
      uploadTrigger: true,
      triggerReset: false,
      uploading: false,
      progress: null,
      hasUploadedOnce: false,
    };
  },
  computed: {
    apiHost() {
      return this.$api_host;
    },
    uploadParams() {
      return {
        description: 'simple-mode-upload',
        validated: false,
        run_model: false,
        validation_set: false,
        label: false,
      };
    },
  },
  async mounted() {
    await this.loadExistingTerms();
  },
  methods: {
    rgbCss(rgb) {
      return `rgb(${rgb || '200,200,200'})`;
    },
    hexFromRgb(rgb) {
      return rgbToHex(rgb);
    },
    chipLabel(chip) {
      return this.normalizeChip(chip);
    },
    termColorForChip(chip) {
      const name = this.normalizeChip(chip);
      const term = this.terms.find((t) => t.name.toLowerCase() === name.toLowerCase());
      return term?.color || DEFAULT_LABEL_COLORS[0];
    },
    normalizeChip(chip) {
      if (chip == null) {
        return '';
      }
      if (typeof chip === 'string') {
        return chip.trim();
      }
      if (typeof chip === 'object' && chip.name) {
        return String(chip.name).trim();
      }
      return String(chip).trim();
    },
    openFilePicker() {
      if (this.terms.length === 0 || this.uploading) {
        return;
      }
      this.$refs.uploader?.openFilePicker();
    },
    async loadExistingTerms() {
      const settings = await this.$api.setting.all();
      const row = (settings || []).find((s) => s && s.name === ON_UPLOAD_LLM_JSON);
      let findQuery = '';
      if (row && row.value) {
        try {
          const parsed = JSON.parse(row.value);
          findQuery = parsed.find_query || '';
        } catch {
          findQuery = '';
        }
      }
      const names = findQuery
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (!names.length) {
        return;
      }
      const labels = await this.$api.label.fetchByNames(names);
      const byName = {};
      (labels || []).forEach((l) => {
        byName[String(l.name).toLowerCase()] = l;
      });
      this.terms = names.map((name, i) => {
        const existing = byName[name.toLowerCase()];
        return {
          name,
          color: existing?.color || DEFAULT_LABEL_COLORS[i % DEFAULT_LABEL_COLORS.length],
          labelId: existing?.id || null,
        };
      });
      this.termChips = names.slice();
    },
    onTermsChanged(chips) {
      const names = (chips || []).map(this.normalizeChip).filter(Boolean);
      const unique = [...new Set(names)];
      const used = this.terms.map((t) => t.color);
      const next = unique.map((name) => {
        const prev = this.terms.find((t) => t.name.toLowerCase() === name.toLowerCase());
        if (prev) {
          return { ...prev, name };
        }
        const color = pickRandomAvailableColors(1, used)[0] || DEFAULT_LABEL_COLORS[0];
        used.push(color);
        return { name, color, labelId: null };
      });
      this.terms = next;
      this.termChips = unique;
    },
    setTermColor(name, hex) {
      const color = hexToRgb(hex);
      this.terms = this.terms.map((t) => (t.name === name ? { ...t, color } : t));
    },
    async ensureLabelsAndUploadSetting() {
      for (const term of this.terms) {
        if (term.labelId) {
          await this.$api.label.patch({
            id: term.labelId,
            name: term.name,
            color: term.color,
            type: 1,
          });
        } else {
          const created = await this.$api.label.add({
            name: term.name,
            color: term.color,
            type: 1,
          });
          if (created && created.id) {
            term.labelId = created.id;
          }
        }
      }
      const findQuery = this.terms.map((t) => t.name).join(',');
      const payload = {
        enabled: true,
        do_find: true,
        do_verify: false,
        do_llm_question: false,
        do_llm_prompt_boxes: false,
        find_query: findQuery,
        category_label_id: null,
        llm_question: '',
        llm_question_meta_key: '',
        llm_prompt_boxes_text: '',
        llm_prompt_boxes_label_id: null,
      };
      await this.$api.setting.patch({
        name: ON_UPLOAD_LLM_JSON,
        value: JSON.stringify(payload),
      });
      // Keep ordered pipeline in sync so uploads use the LLM find step.
      await this.$api.setting.patch({
        name: ON_UPLOAD_PIPELINE_JSON,
        value: JSON.stringify([{
          type: 'llm',
          do_find: true,
          do_verify: false,
          do_llm_question: false,
          do_llm_prompt_boxes: false,
          find_query: findQuery,
          category_label_id: null,
          llm_question: '',
          llm_question_meta_key: '',
          llm_prompt_boxes_text: '',
          llm_prompt_boxes_label_id: null,
        }]),
      });
    },
    async onFilesSelected(selected) {
      if (!selected || this.uploading) {
        return;
      }
      if (!this.terms.length) {
        this.triggerReset = !this.triggerReset;
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('simple_mode.terms_required'));
        return;
      }
      await this.startUpload();
    },
    async startUpload() {
      if (this.uploading) {
        return;
      }
      this.uploading = true;
      this.progress = 0;
      try {
        await this.ensureLabelsAndUploadSetting();
        this.uploadTrigger = !this.uploadTrigger;
        this.$emit('upload-started', this.terms.map((t) => t.name));
      } catch (e) {
        this.uploading = false;
        this.progress = null;
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('simple_mode.upload_prepare_failed'));
      }
    },
    onUploadComplete() {
      this.uploading = false;
      this.progress = null;
      this.hasUploadedOnce = true;
      this.triggerReset = !this.triggerReset;
      this.$emit('upload-complete');
      this.$store.dispatch('snackbars/add_success_snack', this.$t('simple_mode.upload_done'));
    },
    onSuccess() {},
    onFailure() {
      this.uploading = false;
      this.progress = null;
      this.$store.dispatch('snackbars/add_failed_snack', this.$t('simple_mode.upload_failed'));
    },
  },
};
</script>

<style scoped>
.term-color-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
  display: inline-block;
  vertical-align: middle;
}
.term-chip {
  padding-right: 2px;
}
.simple-upload-terms__uploader {
  display: none;
}
</style>
